import type { Millimes, PaymentMethod } from '@beta-assur/shared';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Field, PageHeader } from '../components/page';
import { Empty, Forbidden, QueryView } from '../components/states';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import { useTenant } from '../lib/referentials';

// Journal de caisse (BA-073) : paiements saisis au portail un jour donné, totaux par mode, à
// imprimer pour le rapprochement de fin de journée. L'agent ne voit que ses propres saisies.

interface CashJournal {
  date: string;
  items: {
    id: string;
    at: string;
    amount: Millimes;
    method: PaymentMethod;
    reference: string | null;
    policyNumber: string | null;
    installmentId: string | null;
    recordedBy: string | null;
  }[];
  totals: { method: PaymentMethod; count: number; amount: Millimes }[];
  total: number;
}

export function CashPage() {
  const { t, language, count } = useI18n();
  const auth = useAuth();
  const tz = useTenant().data?.timezone;
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const query = useApiQuery<CashJournal>(
    auth.can('recordPayment') ? '/v1/payments/journal' : null,
    { date },
  );
  if (!auth.can('recordPayment')) return <Forbidden />;

  return (
    <>
      <PageHeader
        title={t.cash.title}
        actions={
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            {t.cash.print}
          </Button>
        }
      />
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3">
          <Field label={t.cash.date}>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <p className="text-sm text-muted-foreground">
            {auth.state.user?.role === 'agent' ? t.cash.mine : t.cash.team}
          </p>
        </CardContent>
      </Card>
      <QueryView query={query}>
        {(data) => (
          <div className="flex flex-col gap-4">
            <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label={t.cash.totals}>
              <li className="rounded-xl border border-border bg-card p-4 shadow-card">
                <p className="text-sm text-muted-foreground">{t.cash.total}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {money(data.total, language)}
                </p>
              </li>
              {data.totals.map((m) => (
                <li
                  key={m.method}
                  className="rounded-xl border border-border bg-card p-4 shadow-card"
                >
                  <p className="text-sm text-muted-foreground">
                    {t.paymentMethods[m.method]} ({count(m.count, t.cash.payments)})
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">
                    {money(m.amount, language)}
                  </p>
                </li>
              ))}
            </ul>
            <Card>
              {data.items.length === 0 ? (
                <Empty>{t.cash.none}</Empty>
              ) : (
                <Table>
                  <THead>
                    <tr>
                      <TH>{t.cash.time}</TH>
                      <TH>{t.portfolio.policyNumber}</TH>
                      <TH>{t.cash.method}</TH>
                      <TH>{t.cash.reference}</TH>
                      <TH className="text-end">{t.cash.amount}</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {data.items.map((p) => (
                      <TR key={p.id}>
                        <TD className="whitespace-nowrap">{instant(p.at, tz, language)}</TD>
                        <TD>
                          {p.installmentId ? (
                            <Link
                              to="/echeances/$id"
                              params={{ id: p.installmentId }}
                              className="underline"
                            >
                              {p.policyNumber ?? '-'}
                            </Link>
                          ) : (
                            (p.policyNumber ?? '-')
                          )}
                        </TD>
                        <TD>{t.paymentMethods[p.method]}</TD>
                        <TD>{p.reference ?? ''}</TD>
                        <TD className="text-end tabular-nums">{money(p.amount, language)}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </Card>
          </div>
        )}
      </QueryView>
    </>
  );
}
